import "iconify-icon"
import commonStyleUrl from "../common.css?url"
import { getElement } from "../lib/get-elements"
import { type ObservedAttribute, html } from "../lib/utils"
import {
	WithAttributeProps,
	boolean,
	pickList,
	string,
} from "../mixins/with-attributes-props"

const template = html`
	<style>
		@import url(${commonStyleUrl});
    .icon {
      display: none;
    }
		[data-with-icon] .icon {
			display: inline;
		}
    :host(:not([loading])) .main:not([data-with-icon]) .prefix {
      display: none;
    }
    .loader {
      display: none;
    }
    .icon {
      display: inline;
    }
    slot[name="loading"] {
      display: none;
    }
    :host([loading]) {
      .icon {
        display: none;
      }
      .loader {
        display: inline;
      }
      [data-has-loading-content] {
        slot {
          display: none;
        }
        slot[name="loading"] {
          display: unset;
        }
      }
    }
    .main {
      display: inline-flex;
      gap: var(--space-xs);
    }
	</style>
	<button class="button main" type="submit">
		<span class="prefix"
			><span class="icon"
				><slot name="icon"><iconify-icon inline></iconify-icon></slot></span
			><x-loader class="loader"></x-loader></span
		><slot></slot><slot name="loading"></slot>
	</button>
`

export class HTMLLoadingButtonElement extends WithAttributeProps(HTMLElement, {
	icon: string(),
	type: pickList({ default: "button", values: ["button", "submit", "reset"] }),
	loading: boolean(),
	disabled: boolean(),
}) {
	#iconSlot: HTMLSlotElement
	#icon
	#button
	#loadingSlot
	constructor() {
		super()
		let shadowRoot = this.attachShadow({ mode: "closed" })
		shadowRoot.appendChild(template.content.cloneNode(true))
		this.#icon = getElement(".icon iconify-icon", shadowRoot)
		this.#button = getElement("button", shadowRoot, HTMLButtonElement)
		this.#loadingSlot = getElement(
			'slot[name="loading"]',
			this.#button,
			HTMLSlotElement,
		)
		this.#iconSlot = getElement(
			"slot[name=icon]",
			this.#button,
			HTMLSlotElement,
		)
		this.#updateButtonData()
	}

	connectedCallback() {
		this.#loadingSlot.addEventListener("slotchange", this.#updateButtonData)
		this.#iconSlot.addEventListener("slotchange", this.#updateIconData)
	}

	disconnectedCallback() {
		this.#loadingSlot.removeEventListener("slotchange", this.#updateButtonData)
	}

	#updateButtonData = () => {
		if (this.#loadingSlot.assignedElements().length > 0) {
			this.#button.dataset.hasLoadingContent = "true"
		} else {
			delete this.#button.dataset.hasLoadingContent
		}
	}

	#updateIconData = () => {
		if (this.icon != null || this.#iconSlot.assignedElements().length > 0) {
			this.#button.dataset.withIcon = "true"
		} else {
			delete this.#button.dataset.withIcon
		}
	}

	static get observedAttributes() {
		return ["icon", "type", "loading", "disabled"] as const
	}

	attributeChangedCallback(
		name: ObservedAttribute<typeof HTMLLoadingButtonElement>,
	) {
		switch (name) {
			case "icon":
				if (this.icon == null) {
					this.#icon.removeAttribute("icon")
				} else {
					this.#icon.setAttribute("icon", this.icon)
				}
				this.#updateIconData()
				break
			case "type":
				this.#button.setAttribute("type", this.type)
				break
			case "loading":
			case "disabled":
				this.#button.disabled = this.loading || this.disabled
				break
		}
	}
}

customElements.define("x-loading-button", HTMLLoadingButtonElement)
